/**
 * Display-only cleanup for specification labels (staged product UI, etc.).
 */

function escapeRegex(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** "SensorSize" / "general_section" → "Sensor Size" / "general section". Preserves already-spaced titles. */
export function humanizeSpecKey(k: string): string {
  const t = k.trim();
  if (!t) return t;
  if (/\s/.test(t)) {
    return t.replace(/_/g, ' ').replace(/\s+/g, ' ').trim();
  }
  return t
    .replace(/([A-Z])/g, ' $1')
    .replace(/_/g, ' ')
    .trim()
    .replace(/\s+/g, ' ');
}

/**
 * Collapse CSS letter-spacing artifacts: "S U P P L I E D  A C C E S S O R I E S"
 * → "Supplied Accessories"
 */
export function collapseLetterSpacedWords(s: string): string {
  if (!s?.trim()) return s;
  return s.replace(/\b([A-Za-z](?:\s+[A-Za-z]){2,})\b/g, (match) => {
    const collapsed = match.replace(/\s+/g, '');
    return collapsed.charAt(0).toUpperCase() + collapsed.slice(1).toLowerCase();
  });
}

/** Remove superscript footnote markers (e.g. Dimensions⁶) */
export function stripUnicodeSuperscripts(s: string): string {
  return s.replace(/[\u00B2\u00B3\u00B9\u2070-\u209F]+/g, '').trim();
}

/**
 * When the field label repeats the section title ("General Section - Connector"
 * under section "General Section"), show only the field part ("Connector").
 */
export function stripRedundantSectionPrefix(sectionKey: string, fieldLabel: string): string {
  const label = fieldLabel.trim();
  const section = humanizeSpecKey(sectionKey);
  if (!section || !label) return label;

  const re = new RegExp(`^${escapeRegex(section)}\\s*-\\s*`, 'i');
  if (re.test(label)) {
    return label.replace(re, '').trim();
  }

  // "General Section - X" under section key "General" (humanized)
  const general = /^general\s*-\s*/i;
  if (/^general$/i.test(section.trim()) && /^general\s+section\s*-\s*/i.test(label)) {
    return label.replace(/^general\s+section\s*-\s*/i, '').trim();
  }

  return label;
}

/** Full label pipeline for a row under a section */
export function formatSpecFieldLabel(sectionKey: string, rawFieldKey: string): string {
  let label = humanizeSpecKey(rawFieldKey);
  label = stripRedundantSectionPrefix(sectionKey, label);
  label = collapseLetterSpacedWords(label);
  label = stripUnicodeSuperscripts(label);
  return label;
}

/** Section title for headings */
export function formatSpecSectionTitle(sectionKey: string): string {
  return stripUnicodeSuperscripts(collapseLetterSpacedWords(humanizeSpecKey(sectionKey)));
}
